import { ref } from 'vue'
import { experiencesService } from '@/services/experiences'
import { answerRowsToCsv } from '@/utils/json-to-csv'
import type { ExperienceExportResponse } from '@/types/experience-export'

export function useExperienceDataExport() {
  const exportData = ref<ExperienceExportResponse | null>(null)
  const loading = ref(false)
  const error = ref<string | null>(null)
  const visualizerOpen = ref(false)

  async function fetchExportData(lineageId: string): Promise<ExperienceExportResponse | null> {
    if (exportData.value && exportData.value.experience.lineageId === lineageId) {
      return exportData.value
    }
    loading.value = true
    error.value = null
    try {
      const data = await experiencesService.getExperienceExport(lineageId)
      exportData.value = data
      return data
    } catch (err: unknown) {
      const message =
        err && typeof err === 'object' && 'response' in err
          ? ((err as { response?: { data?: { error?: string } } }).response?.data?.error ?? 'Failed to fetch export data.')
          : 'Failed to fetch export data.'
      error.value = message
      return null
    } finally {
      loading.value = false
    }
  }

  async function downloadCsv(lineageId: string): Promise<void> {
    const data = await fetchExportData(lineageId)
    if (!data) return

    const sessionsLookup = new Map(data.sessions.map((s) => [s.sessionId, s]))
    const campaignsLookup = new Map(data.campaigns.map((c) => [c.campaignId, c]))

    const csv = answerRowsToCsv(data.answers, sessionsLookup, campaignsLookup)
    const blob = new Blob([csv], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${data.experience.name.replace(/[^a-zA-Z0-9-_]/g, '_')}-answers-${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  async function openVisualizer(lineageId: string): Promise<void> {
    const data = await fetchExportData(lineageId)
    if (!data) return
    visualizerOpen.value = true
  }

  function closeVisualizer(): void {
    visualizerOpen.value = false
  }

  return {
    exportData,
    loading,
    error,
    visualizerOpen,
    downloadCsv,
    openVisualizer,
    closeVisualizer,
  }
}
